/* eslint-disable react/prop-types */
// Shared Clio / DynamoDB dataset for the whole app.
//
// This used to live in `layouts/tables`, which meant navigating away from the
// Data page unmounted the component and threw away every loaded row. Hoisting it
// above the routes lets the Data page and the Exposure page share one load:
// moving between them is instant, while a browser reload remounts this provider
// empty and therefore reloads everything.
//
// The dataset itself is far too large for a cookie; only the date range and the
// "last loaded" stamp are persisted there (see `utils/cookies`).

import { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import dayjs from "dayjs";

import authorsTableData from "layouts/tables/data/authorsTableData";
import mattersTableData from "layouts/tables/data/mattersTableData";
import activitiesTableData from "layouts/tables/data/activitiesDataTable";
import persistedMattersDataTable from "layouts/tables/data/persistedMattersDataTable";
import persistedPaymentsDataTable from "layouts/tables/data/persistedPaymentsDataTable";
import persistedExpensesDataTable from "layouts/tables/data/persistedExpensesDataTable";

import { getCookie, setCookie, REPORT_PREFS_COOKIE } from "utils/cookies";

const formatDate = (date) => (date ? dayjs(date).format("YYYY-MM-DD") : null);
const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

function filterBillsByActivities(billsData, activitiesData) {
  // 1. Collect bill numbers from activities
  const activityBillNumbers = new Set(
    activitiesData.filter((a) => a?.bill?.number).map((a) => String(a.bill.number))
  );

  // 2. Keep only bills that exist in activities
  return billsData.filter((bill) => activityBillNumbers.has(String(bill.number)));
}

const DEFAULT_ISSUED_AFTER = "2025-01-01";

const savedPrefs = getCookie(REPORT_PREFS_COOKIE);

const ReportDataContext = createContext(undefined);

export const ReportDataProvider = ({ children }) => {
  const [issuedAfter, setIssuedAfter] = useState(() =>
    savedPrefs?.issuedAfter ? dayjs(savedPrefs.issuedAfter) : dayjs(DEFAULT_ISSUED_AFTER)
  );
  const [issuedBefore, setIssuedBefore] = useState(() =>
    savedPrefs?.issuedBefore ? dayjs(savedPrefs.issuedBefore) : dayjs()
  );
  const [lastLoadedAt, setLastLoadedAt] = useState(savedPrefs?.lastLoadedAt || null);

  const [columns, setColumns] = useState([]);
  const [rows, setRows] = useState([]);
  const [mattersColumns, setMattersColumns] = useState([]);
  const [mattersRows, setMattersRows] = useState([]);
  const [activitiesColumns, setActivitiesColumns] = useState([]);
  const [activitiesRows, setActivitiesRows] = useState([]);
  const [persistedMattersColumns, setPersistedMattersColumns] = useState([]);
  const [persistedMattersRows, setpersistedMattersRows] = useState([]);
  const [loadingpersistedMatters, setLoadingpersistedMatters] = useState(false);
  const [persistedPaymentsColumns, setPersistedPaymentsColumns] = useState([]);
  const [persistedPaymentsRows, setpersistedPaymentsRows] = useState([]);
  const [loadingpersistedPayments, setLoadingpersistedPayments] = useState(false);
  const [persistedExpensesColumns, setPersistedExpensesColumns] = useState([]);
  const [persistedExpensesRows, setpersistedExpensesRows] = useState([]);
  const [loadingpersistedExpenses, setLoadingpersistedExpenses] = useState(false);
  const [loadingBills, setLoadingBills] = useState(false);
  const [loadingMatters, setLoadingMatters] = useState(false);
  const [loadingActivities, setLoadingActivities] = useState(false);
  const [pKeyeddata, setpKeyeddata] = useState({});
  const [pdata, setpdata] = useState({});
  const [activityKeyedData, setactivityKeyedData] = useState([]);
  const [activityAttorneyMatter, setactivityAttorneyMatter] = useState([]);
  const [combinedInvalidActivities, setcombinedInvalidActivities] = useState([]);
  const [billsData, setBillsData] = useState([]);
  const [mattersOriginData, setMattersOriginData] = useState([]);
  const [activitiesOriginData, setActivitiesOriginData] = useState([]);

  // Kept stable so the report and the exposure aggregation are not recomputed on
  // every unrelated re-render.
  const billedBills = useMemo(
    () => filterBillsByActivities(billsData, activitiesOriginData),
    [billsData, activitiesOriginData]
  );

  const loadBillsData = async () => {
    setLoadingBills(true);
    const { columns, rows, billsData } = await authorsTableData({
      issuedAfter: formatDate(issuedAfter),
      issuedBefore: formatDate(issuedBefore),
    });
    setColumns(columns);
    setRows(rows);
    let cleanedData = billsData.data.filter(
      (item) => item && item.state !== "void" && item.state !== "deleted"
    );

    setBillsData(cleanedData);
    setLoadingBills(false);
  };

  const loadMattersData = async () => {
    setLoadingMatters(true);
    const { mattersColumns, mattersRows, mattersData } = await mattersTableData({
      issuedAfter: formatDate(issuedAfter),
      issuedBefore: formatDate(issuedBefore),
    });

    setMattersColumns(mattersColumns);
    setMattersRows(mattersRows);

    setLoadingMatters(false);
    let cleanedData = mattersData.data.filter(
      (item) => item && item.state !== "void" && item.state !== "deleted"
    );
    cleanedData = mattersData.data.filter(
      (item) =>
        item &&
        item.state !== "void" &&
        item.state !== "deleted" &&
        item.originating_attorney !== null
    );
    setMattersOriginData(cleanedData);
  };

  const loadActivitiesData = async () => {
    setLoadingActivities(true);
    const { activitiesColumns, activitiesRows, activitiesData } = await activitiesTableData({
      issuedAfter: formatDate(issuedAfter),
      issuedBefore: formatDate(issuedBefore),
    });
    setActivitiesColumns(activitiesColumns);
    setActivitiesRows(activitiesRows);
    let cleanedData = activitiesData.data.filter((item) => {
      const type = item?.type?.trim().toLowerCase();

      return item && item.state !== "void" && item.state !== "deleted" && type !== "expenseentry";
    });

    setActivitiesOriginData(cleanedData);
    setLoadingActivities(false);
    const activityKeyedData = {};
    const activityAttorneyMatter = {};

    // Create more comprehensive invalid activities tracking
    const invalidActivitiesByAttorney = {};
    const unassignedActivities = []; // For data without attorney info

    // First, process ALL activities and check for data issues
    activitiesData.data.forEach((item, index) => {
      try {
        // Skip if item is null or undefined
        if (!item) {
          unassignedActivities.push({
            _errorReason: "Null or undefined activity",
            _index: index,
          });
          return;
        }
        if (item.type === "ExpenseEntry") {
          return;
        }

        // Step 1: Check for all required fields but only flag errors for matter issues
        const hasBill = item.bill && typeof item.bill === "object";
        const hasUser = item.user && typeof item.user === "object" && item.user.name;
        const hasMatter = item.matter && typeof item.matter === "object";
        const hasDisplayNumber = hasMatter && item.matter.display_number;

        // Only check for matter-related errors
        let errorReason = null;
        if (!hasMatter) errorReason = "Missing matter object";
        else if (!hasDisplayNumber) errorReason = "Missing display_number";

        if (errorReason) {
          // This is an invalid activity - classify by attorney if possible
          if (hasUser) {
            const attorneyName = item.user.name;
            if (!invalidActivitiesByAttorney[attorneyName]) {
              invalidActivitiesByAttorney[attorneyName] = [];
            }
            invalidActivitiesByAttorney[attorneyName].push({
              ...item,
              _errorReason: errorReason,
              _index: index,
            });
          } else {
            // No attorney information - save separately
            unassignedActivities.push({
              ...item,
              _errorReason: errorReason,
              _index: index,
            });
          }

          // For items with issues, don't process further
          return;
        }

        // Process valid activities for reporting (only if they have bill and user)
        if (hasBill && hasUser) {
          // Step 2: Process key data for reporting
          if (item.bill.number in activityKeyedData) {
            if (item.user.name in activityKeyedData[item.bill.number]) {
              activityKeyedData[item.bill.number][item.user.name] += item.total || 0;
            } else {
              activityKeyedData[item.bill.number][item.user.name] = item.total || 0;
            }
          } else {
            activityKeyedData[item.bill.number] = {
              [item.user.name]: item.total || 0,
            };
          }

          // Step 3: Process matter tracking (only for valid activities)
          if (item.total && hasDisplayNumber) {
            if (item.user.name in activityAttorneyMatter) {
              activityAttorneyMatter[item.user.name].add(item.matter.display_number);
            } else {
              activityAttorneyMatter[item.user.name] = new Set([item.matter.display_number]);
            }
          }
        }
      } catch (error) {
        console.error(`Error processing activity at index ${index}:`, error);

        // Add to unassigned errors if we can't process it
        unassignedActivities.push({
          ...item,
          _errorReason: `Processing error: ${error.message}`,
          _index: index,
        });
      }
    });

    setactivityKeyedData(activityKeyedData);

    setactivityAttorneyMatter(activityAttorneyMatter);
    const combinedInvalidActivities = { ...invalidActivitiesByAttorney };

    // Add unassigned activities to a special category
    if (unassignedActivities.length > 0) {
      combinedInvalidActivities["__UNASSIGNED__"] = unassignedActivities;
    }
    setcombinedInvalidActivities(combinedInvalidActivities);
    Object.entries(combinedInvalidActivities).forEach(([category, activities]) => {
      console.log(`${category}: ${activities.length} invalid activities`);
    });
  };

  const loadPersistedMattersData = async () => {
    setLoadingpersistedMatters(true);
    const { persistedMattersColumns, persistedMattersRows, persistedMattersData } =
      await persistedMattersDataTable();
    setPersistedMattersColumns(persistedMattersColumns);
    setpersistedMattersRows(persistedMattersRows);
    setLoadingpersistedMatters(false);
    if (!Array.isArray(mattersRows)) setMattersRows([]);
    const mattersRawDataClean = persistedMattersData.map((item) => {
      return Object.entries(item).reduce((a, b) => {
        return { ...a, [b[0]]: b[1] === "None" ? "" : b[1] };
      }, {});
    });
    const mattersKeyed = {};
    mattersRawDataClean.forEach((item) => {
      if (!item || !item.id) return; // Skip invalid items

      if (item.id in mattersKeyed) {
        // If we already have an entry for this ID
        const existingItem = mattersKeyed[item.id];

        // If the existing item is not an array, convert it to one
        if (!Array.isArray(existingItem)) {
          mattersKeyed[item.id] = [existingItem];
        }

        // Add the new item to the array
        mattersKeyed[item.id].push(item);
      } else {
        // First time seeing this ID
        mattersKeyed[item.id] = item;
      }
    });
    setpKeyeddata((prev) => ({
      ...prev,
      matters: mattersKeyed,
    }));
    setpdata((prev) => ({
      ...prev,
      matters: mattersRawDataClean,
    }));
  };

  const loadPersistedPaymentsData = async () => {
    setLoadingpersistedPayments(true);
    const { persistedPaymentsColumns, persistedPaymentsRows, persistedPaymentsData } =
      await persistedPaymentsDataTable();
    setPersistedPaymentsColumns(persistedPaymentsColumns);
    setpersistedPaymentsRows(persistedPaymentsRows);
    setLoadingpersistedPayments(false);
    const paymentsKeyed = {};
    const paymentsRawDataClean = persistedPaymentsData.map((item) => {
      return Object.entries(item).reduce((a, b) => {
        return { ...a, [b[0]]: b[1] === "None" ? "" : b[1] };
      }, {});
    });
    paymentsRawDataClean.forEach((item) => {
      if (!item || !item.id) return; // Skip invalid items

      const invoiceId = item.id; // The invoice number is now the primary ID

      // If we already have an entry for this invoice
      if (invoiceId in paymentsKeyed) {
        const existingItem = paymentsKeyed[invoiceId];

        // If the existing item is not an array, convert it to one
        if (!Array.isArray(existingItem)) {
          paymentsKeyed[invoiceId] = [existingItem];
        }

        // Add the new item to the array
        paymentsKeyed[invoiceId].push(item);
      } else {
        // First time seeing this invoice, store as a single object
        paymentsKeyed[invoiceId] = item;
      }
    });
    setpKeyeddata((prev) => ({
      ...prev,
      payments: paymentsKeyed,
    }));
    setpdata((prev) => ({
      ...prev,
      payments: paymentsRawDataClean,
    }));
  };

  const loadPersistedExpensesData = async () => {
    setLoadingpersistedExpenses(true);
    const { persistedExpensesColumns, persistedExpensesRows, persistedExpensesData } =
      await persistedExpensesDataTable();
    setPersistedExpensesColumns(persistedExpensesColumns);
    setpersistedExpensesRows(persistedExpensesRows);
    setLoadingpersistedExpenses(false);
    const expensesRawDataClean = persistedExpensesData.map((item) => {
      return Object.entries(item).reduce((a, b) => {
        return { ...a, [b[0]]: b[1] === "None" ? "" : b[1] };
      }, {});
    });
    const expensesKeyed = {};
    expensesRawDataClean.forEach((item) => {
      if (!item || !item.attorney || !item.date) return; // Skip invalid items

      if (item.attorney in expensesKeyed) {
        expensesKeyed[item.attorney][item.date] = item;
      } else {
        expensesKeyed[item.attorney] = { [item.date]: item };
      }
    });
    setpKeyeddata((prev) => ({
      ...prev,
      expenses: expensesKeyed,
    }));

    setpdata((prev) => ({
      ...prev,
      expenses: expensesRawDataClean,
    }));
  };

  // Only the date range and the load stamp go to the cookie — the dataset itself
  // is orders of magnitude past the ~4KB cookie limit.
  const persistPrefs = (loadedAt) => {
    setCookie(REPORT_PREFS_COOKIE, {
      issuedAfter: formatDate(issuedAfter),
      issuedBefore: formatDate(issuedBefore),
      lastLoadedAt: loadedAt,
    });
  };

  const stampLoaded = () => {
    const loadedAt = Date.now();
    setLastLoadedAt(loadedAt);
    persistPrefs(loadedAt);
  };

  const handleSearch = async () => {
    // Awaited as a group so the "last loaded" stamp only lands once every request
    // has actually settled. A failure leaves the previous stamp in place rather
    // than claiming a load that did not finish.
    try {
      await Promise.all([
        loadBillsData(),
        loadMattersData(),
        loadActivitiesData(),
        loadPersistedMattersData(),
        loadPersistedPaymentsData(),
        loadPersistedExpensesData(),
      ]);
      stampLoaded();
    } catch (error) {
      console.error("Search failed to load the full dataset", error);
    }
  };

  // Runs at most once per page load, no matter how many pages ask for the data.
  // A browser reload remounts this provider, which resets the guard and reloads
  // everything.
  const hasLoadedRef = useRef(false);
  const ensureLoaded = () => {
    if (hasLoadedRef.current) return;
    hasLoadedRef.current = true;

    const runWithDelay = async () => {
      await loadPersistedMattersData();
      await loadPersistedPaymentsData();
      await loadPersistedExpensesData();
      await loadBillsData();
      await delay(1000);
      await loadMattersData();
      await delay(1000);
      await loadActivitiesData();
      await delay(1000);
      stampLoaded();
    };

    runWithDelay().catch((error) => {
      console.error("Initial data load failed", error);
    });
  };

  // Keep the stored range in sync when the user edits the pickers, so a reload
  // comes back on the same dates even if no load has been run since.
  useEffect(() => {
    persistPrefs(lastLoadedAt);
  }, [issuedAfter, issuedBefore]);

  // Deliberately not memoized: `handleSearch` and `ensureLoaded` close over the
  // current date range, so caching the value object would make Search run against
  // whatever dates were set on the first render. The provider only re-renders when
  // its own state changes, and every consumer reads that state anyway.
  const value = {
    // filters
    issuedAfter,
    setIssuedAfter,
    issuedBefore,
    setIssuedBefore,
    lastLoadedAt,
    // actions
    handleSearch,
    ensureLoaded,
    // table data
    columns,
    rows,
    mattersColumns,
    mattersRows,
    activitiesColumns,
    activitiesRows,
    persistedMattersColumns,
    persistedMattersRows,
    persistedPaymentsColumns,
    persistedPaymentsRows,
    persistedExpensesColumns,
    persistedExpensesRows,
    // derived / report data
    billedBills,
    mattersOriginData,
    activitiesOriginData,
    activityKeyedData,
    activityAttorneyMatter,
    combinedInvalidActivities,
    pKeyeddata,
    pdata,
    // loading flags
    loadingBills,
    loadingMatters,
    loadingActivities,
    loadingpersistedMatters,
    loadingpersistedPayments,
    loadingpersistedExpenses,
  };

  return <ReportDataContext.Provider value={value}>{children}</ReportDataContext.Provider>;
};

export const useReportData = () => {
  const context = useContext(ReportDataContext);

  if (!context) {
    throw new Error("useReportData must be used inside a ReportDataProvider");
  }

  return context;
};

export default ReportDataContext;
